import { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Tooltip, Typography } from "@material-tailwind/react"
import { CheckBadgeIcon } from '@heroicons/react/24/solid'
import { ChatBubbleBottomCenterTextIcon, XCircleIcon } from '@heroicons/react/24/outline'
import RefDropdown from './RefDropdown'
import { RefMethodModal } from './RefMethodModal'
import DataTableShell from '../../../../Components/table/DataTableShell'
import TableToolbar from '../../../../Components/table/TableToolbar'
import TableSearch from '../../../../Components/table/TableSearch'
import { TableEmptyRow, TableIdCell, TableSkeletonRows } from '../../../../Components/table/TableCells'
import useClientTable from '../../../../Components/table/useClientTable'

const TABLE_HEAD = [
    { key: 'orderId', label: 'Order ID' },
    { key: 'refundMethod', label: 'Refund Method' },
    { key: 'actions', label: 'Actions' },
    { key: 'rmaId', label: 'RMA ID' },
    { key: 'sku', label: 'SKU' },
    { key: 'returnReason', label: 'Return Reason' },
    { key: 'buyerComments', label: <>Buyer<br/>Comments</> },
    { key: 'outboundShipping', label: <>Outbound<br/>Shipping</> },
    { key: 'giftBadge', label: <>Gift<br/>Badge</> },
    { key: 'replacementBadge', label: <>Replacement<br/>Badge</> },
    { key: 'amazonRefunded', label: <>Amazon<br/>Refunded</> },
    { key: 'manuallyRefunded', label: <>Manually<br/>Refunded</> },
    { key: 'refundTotal', label: <>Refund Total<br/>Amount</> },
]

export function ManageReturnsTable({ batchId }) {

    const [orderMap, setOrderMap] = useState({});
    const [refundMethods, setRefundMethods] = useState({});
    const [flattenedBatchList, setFlattenedBatchList] = useState({})
    
    const fetchReturnReason = async (returnReasonCode) => {
        try {
            const response = await axios.get(`https://api-central.outdoorequippedservice.com/api/v1/warehouse-refunds/audits/localize/code/${returnReasonCode}`)
            return response.data.value || 'no return code found'
        } catch (error) {
            console.error("Error fetching return reason:", error)
            return "Return reason code not found"
        }
    }
    
    useEffect(() => {
        const fetchBatchData = async () => {
            try {
                const [nexResponse, acResponse] = await Promise.all([
                    axios.get(`/api/refund-audits/get/${batchId}`),
                    axios.get(`https://api-central.outdoorequippedservice.com/api/v1/warehouse-refunds/audits/batch-content/${batchId}`)
                ])
    
                if (nexResponse.data.message || acResponse.data.message) {
                    console.log("Cannot get batches")
                } else {
                    const nexData = nexResponse.data.result.batchContent
                    const acData = acResponse.data[0].batchDetails
    
                    const tempBatchList = {}
                    const fetchReturnReasonPromises = []

                    const tempOrderMap = {}
                    nexData.forEach((batch, index) => {
                        tempOrderMap[batch.orderId] = index
                    })
                    setOrderMap(tempOrderMap)
                    
                    nexData.forEach(batch => {
                        const acBatch = acData.find(item => item._id === batch._id)
                    
                        if (acBatch && acBatch.manageReturnsData.length > 0) {
                            acBatch.manageReturnsData.forEach(returns => {
                                const orderId = batch?.orderId
                                const rmaId  = returns?.rmaId
                                const sku = returns?.returnRequestItems[0]?.merchantSKU
                                const returnReasonCode = returns?.returnRequestItems[0]?.returnReasonStringId
                                const buyerComments = returns?.returnRequestItems[0]?.customerComments
                                const giftBadge = returns?.giftReturn === false ? "No" : "Yes"
                                const outboundShip = returns?.outboundShipping === false ? "No" : "Yes"

                                const repResolution = returns?.returnRequestItems[0]?.resolution
                                const repOrderId = returns?.returnRequestItems[0]?.replacementOrderId
                                const repBadgeVal = repResolution === 'Replacement' && repOrderId ? 'Yes' : 'No'

                                const amzRefunded =  returns?.amazonRefunded ? 'Yes' : 'No'
                                const manualRefunded = returns?.manuallyRefunded ? 'Yes' : 'No'
                                const refTotalAmt = returns?.refundTotal?.totalAppliedRefund?.currencyValue || '....'

                                const fetchPromise = fetchReturnReason(returnReasonCode).then(returnReasonValue => {
                                tempBatchList[rmaId] = { orderId, rmaId, sku, returnReasonValue, buyerComments, outboundShip, repBadgeVal, giftBadge, amzRefunded, manualRefunded, refTotalAmt }
                                })
                                fetchReturnReasonPromises.push(fetchPromise)
                            })
                        }
                    })
                    await Promise.all(fetchReturnReasonPromises)
                    setFlattenedBatchList(tempBatchList)
                }
            } catch (error) {
                console.error('Error:', error)
            }
        }
        fetchBatchData()
    }, [batchId])

    const flattenedBatchArray = useMemo(() => Object.entries(flattenedBatchList).map(([rmaId, batch]) => ({
        rmaId,
        ...batch
    })).sort((a, b) => orderMap[a.orderId] - orderMap[b.orderId]), [flattenedBatchList, orderMap]);

    const handleSelectChange = (itemId, value) => {
        setRefundMethods(prevMethods => ({
            ...prevMethods,
            [itemId]: value
        }));
    };

    const batchesPerPage = 15
    const {
        searchTerm,
        handleSearch,
        currentPage,
        setCurrentPage,
        pageItems: batchData,
        totalPages,
        totalFiltered,
    } = useClientTable({
        data: flattenedBatchArray,
        pageSize: batchesPerPage,
        filterFn: (item, query) =>
            !query ||
            item.orderId?.toString().toLowerCase().includes(query) ||
            item.rmaId?.toString().toLowerCase().includes(query) ||
            item.sku?.toLowerCase().includes(query),
    })

    return (
        <DataTableShell
            minWidth="1540px"
            dense
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
            toolbar={
                <TableToolbar
                    kicker="Refund audit"
                    title="Manage returns"
                    subtitle={`${totalFiltered} matching return items`}
                    actions={
                        <TableSearch
                            id="manage-returns-search"
                            value={searchTerm}
                            onChange={handleSearch}
                            placeholder="Search order, RMA, or SKU"
                        />
                    }
                />
            }
        >
                    <thead>
                        <tr>
                            {TABLE_HEAD.map(({ key, label }, index) => (
                                <th key={key} className={index === 0 ? 'nx-sticky-col' : ''}>
                                    {label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {flattenedBatchArray.length <= 0 ?
                            <TableSkeletonRows columns={TABLE_HEAD.length} rows={15} /> :
                            batchData.length === 0 ?
                            <TableEmptyRow columns={TABLE_HEAD.length} title="No returns found" description="Try a different order, RMA, or SKU." /> :
                                (batchData.map((item) => (
                                    <tr key={`${item.orderId}-${item.rmaId}`}>
                                        <td className="nx-sticky-col">
                                            <TableIdCell>{item.orderId}</TableIdCell>
                                        </td>
                                        <td>
                                            <RefDropdown
                                                itemId={item.rmaId}
                                                onSelectChange={handleSelectChange}
                                            />
                                        </td>
                                        <td>
                                            <RefMethodModal
                                                data={item}
                                                refundMethod={refundMethods[item.rmaId] || 'regRef'}
                                            />
                                        </td>
                                        <td>
                                            <TableIdCell>{item.rmaId}</TableIdCell>
                                        </td>
                                        <td>{item.sku}</td>
                                        <td>{item.returnReasonValue}</td>
                                        <td>
                                        <Tooltip 
                                        className="border border-blue-gray-50 text-black bg-white px-4 py-3 shadow-xl shadow-black/10"
                                        content={          
                                            <Typography color="blue-gray" className="font-medium">
                                                {item.buyerComments}
                                            </Typography>}>
                                            <ChatBubbleBottomCenterTextIcon className='h-6 w-full font-bold text-blue-500'/>
                                        </Tooltip>

                                        </td>
                                        <td>
                                            {item.outboundShip === 'Yes' ? <CheckBadgeIcon className='h-6 w-full text-green-500'/> 
                                            :  <XCircleIcon className='h-6 w-full text-gray-500'/>}
                                        </td>
                                        <td>
                                            {item.giftBadge === 'Yes' ? <CheckBadgeIcon className='h-6 w-full text-green-500'/> 
                                            :  <XCircleIcon className='h-6 w-full text-gray-500'/>}
                                        </td>
                                        <td>
                                        {item.repBadgeVal === 'Yes' ? <CheckBadgeIcon className='h-6 w-full text-green-500'/> 
                                            :  <XCircleIcon className='h-6 w-full text-gray-500'/>}
                                        </td>
                                        <td>
                                        {item.amzRefunded === 'Yes' ? <CheckBadgeIcon className='h-6 w-full text-green-500'/> 
                                            :  <XCircleIcon className='h-6 w-full text-gray-500'/>}
                                        </td>
                                        <td>
                                        {item.manualRefunded === 'Yes' ? <CheckBadgeIcon className='h-6 w-full text-green-500'/> 
                                            :  <XCircleIcon className='h-6 w-full text-gray-500'/>}
                                        </td>
                                        <td className="nx-cell-num">
                                            $ {item.refTotalAmt}
                                        </td>
                                    </tr>
                                )))         
                            }
                    </tbody>
        </DataTableShell>
    )
}

